import { useState } from 'react';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { Checkbox } from '../../../../components/inputs/Checkbox';
import { FormFieldset } from '../../../../components/inputs/FormFieldset';
import { TextField } from '../../../../components/inputs/TextField';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import { familyRelationsApi } from '../familyRelationsApi';
import type { FamilyRelation } from '../familyRelationTypes';
import { sampleFamilyRelation } from '../utils/sampleFamilyRelation';

/**
 * Create or edit one family relation.
 *
 * Two halves, and the fieldset is what separates them: what the relation is CALLED, and what
 * the family form does when somebody picks it. The second half is the reason this is a catalog
 * rather than a list of names — each box replaces a branch that used to be written against an
 * enum value.
 *
 * No position field: the order these appear in on the family form is assigned by the server
 * and changed by moving the row, not by typing a number here — a rename is not a reorder.
 */
export function FamilyRelationModal({
  open,
  editing,
  onClose,
  onSaved,
}: {
  open: boolean;
  /** Null creates. */
  editing: FamilyRelation | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  // Seeded once, on mount. The parent mounts this only while it is open.
  const [name, setName] = useState(editing?.name ?? '');
  const [description, setDescription] = useState(editing?.description ?? '');
  const [guidance, setGuidance] = useState(editing?.guidance ?? '');
  const [allowsMultiple, setAllowsMultiple] = useState(editing?.allowsMultiple ?? false);
  const [requiresDateOfBirth, setRequiresDateOfBirth] = useState(
    editing?.requiresDateOfBirth ?? false,
  );
  const [collectsOccupation, setCollectsOccupation] = useState(editing?.collectsOccupation ?? false);

  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const valid = name.trim().length > 0;
  const canSave = valid && confirmed && !saving;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    const request = {
      name: name.trim(),
      description: description.trim() || null,
      guidance: guidance.trim() || null,
      allowsMultiple,
      requiresDateOfBirth,
      collectsOccupation,
    };

    try {
      if (editing) await familyRelationsApi.update(editing.id, request);
      else await familyRelationsApi.create(request);

      onSaved();
    } catch (caught) {
      const failure =
        caught instanceof ApiError ? caught : new ApiError('Could not save this relation.', 0);

      setError(failure);
      focusValidationError(failure, { idPrefix: 'family-relation' });
    } finally {
      setSaving(false);
    }
  }

  function generate() {
    const sample = sampleFamilyRelation();

    setName(sample.name);
    setDescription(sample.description);
    setGuidance(sample.guidance);
    setAllowsMultiple(sample.allowsMultiple);
    setRequiresDateOfBirth(sample.requiresDateOfBirth);
    setCollectsOccupation(sample.collectsOccupation);
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editing ? 'Edit relation' : 'New relation'}
      size="lg"
      id="family-relation-modal"
      autogenerate={{ onGenerate: generate, disabled: saving }}
      confirm={{
        id: 'family-relation-confirm',
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      footer={
        <>
          <Button
            id="family-relation-cancel"
            variant="secondary"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </Button>

          <Button
            id="family-relation-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        <TextField
          id="family-relation-name"
          label="Name"
          required
          description="What the reader picks on the family form — Spouse, Father, Guardian."
          value={name}
          onChange={(e) => setName(e.target.value)}
          error={name.trim() ? error?.fieldError('name') : 'Enter a name.'}
          disabled={saving}
        />

        <TextField
          id="family-relation-description"
          label="Description"
          description="One line saying what this relation is. Shown on this list, not on the family form."
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          error={error?.fieldError('description')}
          disabled={saving}
        />

        <TextField
          id="family-relation-guidance"
          label="Form guidance"
          description="A note shown under the family form when this relation is chosen. Mother carries the maiden-name instruction."
          value={guidance}
          onChange={(e) => setGuidance(e.target.value)}
          error={error?.fieldError('guidance')}
          disabled={saving}
        />

        {/*
          A real fieldset rather than a heading over a div: these three belong together and a
          screen reader should announce the group's name with each of them. They are what makes
          this a catalog rather than a list of labels.
        */}
        <FormFieldset legend="What the family form does">
          <label className="flex items-start gap-2.5" htmlFor="family-relation-allows-multiple">
            <Checkbox
              id="family-relation-allows-multiple"
              checked={allowsMultiple}
              onChange={(e) => setAllowsMultiple(e.target.checked)}
              disabled={saving}
              className="mt-0.5"
            />
            <span className="flex flex-col gap-0.5">
              <span className="text-sm text-slate-700">Allow several per record</span>
              <span className="text-xs text-slate-500">
                A 201 file can hold any number of people at this relation. Leave it off for a
                father or a mother, where a second row is a data-entry mistake rather than a fact.
              </span>
            </span>
          </label>

          <label className="flex items-start gap-2.5" htmlFor="family-relation-requires-dob">
            <Checkbox
              id="family-relation-requires-dob"
              checked={requiresDateOfBirth}
              onChange={(e) => setRequiresDateOfBirth(e.target.checked)}
              disabled={saving}
              className="mt-0.5"
            />
            <span className="flex flex-col gap-0.5">
              <span className="text-sm text-slate-700">Require a date of birth</span>
              <span className="text-xs text-slate-500">
                The form refuses a member at this relation without one. CS Form 212 asks it of
                children and of nobody else.
              </span>
            </span>
          </label>

          <label className="flex items-start gap-2.5" htmlFor="family-relation-collects-occupation">
            <Checkbox
              id="family-relation-collects-occupation"
              checked={collectsOccupation}
              onChange={(e) => setCollectsOccupation(e.target.checked)}
              disabled={saving}
              className="mt-0.5"
            />
            <span className="flex flex-col gap-0.5">
              <span className="text-sm text-slate-700">Ask for occupational info</span>
              <span className="text-xs text-slate-500">
                Draws the occupation, employer, business address and telephone. Section II asks
                these of the spouse only.
              </span>
            </span>
          </label>
        </FormFieldset>

        {/*
          Said on the form rather than on the tick: these flags change what every future family
          member at this relation is asked, which is a change to what the module collects.
        */}
        <p className="text-xs text-slate-500">
          A new relation becomes a choice on the family form of every personnel record. Deleting
          one removes the choice and leaves the members already recorded against it alone.
        </p>
      </div>
    </Modal>
  );
}
